/**
 * Section 09e — Gandiv Strategy.
 *
 * The rule lives in utils/strategies/gandivEngine.ts (byte-identical backend
 * twin) and the band in utils/gandivBand.ts. Every claim on this page about
 * what does or does not move is asserted by `cd backend && npm run verify:gandiv`.
 */
import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import GuideCallout from '../GuideCallout';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const MARKS: Array<[string, string]> = [
  ['▲ B  /  ▼ S', 'The source close crossed the band: a first entry, or a reversal because the band was crossed before the stop was reached. The new stop is the band’s opposite line at this bar.'],
  ['▲ R  /  ▼ R  (amber)', 'The stop was crossed. The trade is closed and reversed at this bar’s real close, and the new stop comes from the stopped trade (see the chain below).'],
  ['● X  (amber)', 'The stop was crossed, but no stop for the reverse trade was on the right side of the price, so Gandiv is flat until the next band cross. Rare.'],
];

const Section_strategy_gandiv: GuideSection = {
  id: 'strategy-gandiv',
  label: 'Gandiv Strategy',
  group: 'strategies',
  icon: <CallSplitIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Gandiv Strategy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        A breakout of the <strong>higher-timeframe CLN band</strong> with a <strong>fixed stop</strong> taken from
        the band, and a stop-and-reverse that remembers where each trade began.
      </Typography>

      <GuideCallout kind="warning" title="Chart marks only — and not backtested">
        There is no scan, no alert and no auto-trade behind Gandiv. It draws its marks, its band and every trade&apos;s
        Entry and SL lines so you can check each trade by eye. Nothing about its returns has been measured.
      </GuideCallout>

      <H id="gandiv-rule">The rule</H>
      <P>
        <strong>Long:</strong> the source candle&apos;s close crosses <em>above</em> the band&apos;s upper line — it
        closes above it on this bar and did not on the bar before, each bar judged against its own band. The stop is
        the band&apos;s <strong>lower</strong> line at that bar, and it never trails.
      </P>
      <P>
        <strong>Short:</strong> the mirror image — a close crossing <em>below</em> the lower line, stop at the upper line.
      </P>
      <P>
        <strong>While in a trade:</strong> if the source close crosses the stop, the trade is closed and reversed. If
        instead the close crosses the band the other way first, it reverses with a fresh stop from the band at that bar.
        A cross in the direction you are already on does nothing. When the stop and an opposite band cross land on the
        same bar, the stop rule wins. Before the first cross Gandiv is flat.
      </P>

      <H id="gandiv-chain">The stop chain</H>
      <P>
        Every trade remembers the band <strong>at the bar it was opened</strong>. When a trade is stopped, the reverse
        trade&apos;s stop is the opposite line of <em>that</em> band:
      </P>
      <Box component="ol" sx={{ pl: 3, mb: 1.5, '& li': { mb: 0.5, fontSize: '0.95rem', lineHeight: 1.6 } }}>
        <li>Long opened on a band cross when the band was 100–110. Stop = 100.</li>
        <li>The close crosses 100: reverse to short. Its stop = <strong>110</strong>, the band&apos;s upper line when the long was opened.</li>
        <li>That short was opened when the band stood at, say, 95–104. If it is stopped (a close above 110), the new long&apos;s stop = <strong>95</strong>.</li>
      </Box>
      <P>
        If a chained stop is already on the wrong side of the price when the reversal happens (the band has moved a
        long way since), the band&apos;s line at the reversal bar is used instead. If that is on the wrong side too,
        Gandiv goes flat. Both cases are counted in the chart&apos;s notice.
      </P>

      <H id="gandiv-band">The band — and why it is not the CLN context band</H>
      <P>
        The band is the CLN regression body of the higher timeframe mapped on the <strong>Strategies</strong> page
        (4H → 1D, 1D → 1W and so on), at the CLN length set under <strong>Chart Settings → Candle</strong>. There is
        no separate length.
      </P>
      <P>
        The HTF Band chip (the CLN context band) shows each higher-timeframe window&apos;s <em>final</em> body.
        During the window that body is still moving, so a rule judged against it would put marks in history that
        could not have appeared live. Gandiv uses the body <strong>as it was known at each bar&apos;s close</strong>:
        every window that had already closed, plus the current window up to this bar. On the last bar of a window
        the two are the same; inside a window, Gandiv&apos;s band steps bar by bar while the context band sits flat
        at the final value. Turn the HTF Band chip off when you use Gandiv, so you read the band the rule used.
      </P>
      <GuideCallout kind="info" title="Nothing moves once its bar has closed">
        A mark, an Entry line or an SL line on a closed bar never moves or disappears, and nothing appears in history
        where it could not have appeared live. That includes Bar Replay, and the minutes between two refreshes of
        the higher-timeframe data. This is tested on every change, by cutting history at hundreds of points and
        checking that nothing before the cut differs.
      </GuideCallout>

      <H id="gandiv-source">Source candle and prices</H>
      <P>
        <strong>Chart Settings → Gandiv → Source candle</strong> picks whose close is judged against the band:
        Reg (real), HA (the default), CLH, or CLN (the regression body at the chart&apos;s CLN length). The source only
        decides <em>when</em> a cross happened. HA, CLH and CLN prices were never tradeable, so every Entry line
        is at the <strong>real close</strong> of the bar that decided it, and every SL is a band price.
      </P>

      <H id="gandiv-chart">What is drawn</H>
      <Table size="small" sx={{ mb: 2, '& td': { fontSize: '0.9rem', verticalAlign: 'top' } }}>
        <TableHead><TableRow><TableCell>Mark</TableCell><TableCell>Meaning</TableCell></TableRow></TableHead>
        <TableBody>
          {MARKS.map(([m, d]) => (
            <TableRow key={m}><TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{m}</TableCell><TableCell>{d}</TableCell></TableRow>
          ))}
        </TableBody>
      </Table>
      <P>
        <strong>Band:</strong> two thin lines with a light fill between them. When <em>both</em> lines step up
        from the previous candle the band is teal (rising), when both step down it is red (falling); otherwise it
        follows the higher-timeframe regression direction. Fill and lines share the colour (the fill strength is
        <em> Band fill</em> in the settings). <strong>Entry:</strong> a solid line at the entry price, green for long and red for short, from the
        entry bar to the exit bar. <strong>SL:</strong> a dashed amber line over the same bars. Each starts with a
        small label (E 654.59, SL 620.10), and the open trade&apos;s Entry and SL are also labelled on the price axis.
        Every level spans the full width of its bar, so you can read a close directly against the line above or
        below it. Each part can be switched off, and every colour changed, under <strong>Chart Settings → Gandiv</strong>.
      </P>
      <P>
        On a chart at the top of the ladder (1W) there is no higher timeframe, so Gandiv marks nothing and says so.
      </P>
      <P>
        <OpenInApp to="/charts/METAXUSD?interval=240&candle=HA&strategy=gandiv" label="Open METAXUSD 4H with Gandiv on" />
      </P>
    </>
  ),
};

export default Section_strategy_gandiv;
